import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { warn } from '../utils/console';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Check if it's a refresh token error
    if (error.message?.includes('Missing Refresh Token')) {
      warn('Refresh token error caught by ErrorBoundary');
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleLogin = () => {
    // Clear any stored tokens and redirect to login
    localStorage.clear();
    window.location.href = '/login';
  };

  public render() {
    if (this.state.hasError) {
      const isRefreshTokenError = this.state.error?.message?.includes('Missing Refresh Token');
      
      return (
        <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {isRefreshTokenError ? 'Session Expired' : 'Something went wrong'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {isRefreshTokenError 
                ? 'Your session has expired. Please log in again to continue.'
                : 'An unexpected error occurred. Please try refreshing the page.'
              }
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {isRefreshTokenError ? (
                <Button variant="contained" onClick={this.handleLogin}>
                  Go to Login
                </Button>
              ) : (
                <Button variant="contained" onClick={this.handleReload}>
                  Reload Page
                </Button>
              )}
            </Box>
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
